import * as React from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';
import { useGoogleCalendarConnect } from '../settings/googleCalendarConnect';

import { can } from '../../utils/access';
const dismissKey = (organizationId) => `googleCalendarAuthBannerDismissed:${organizationId}`;

function readDismissed(organizationId) {
  if (!organizationId) return false;
  try {
    return sessionStorage.getItem(dismissKey(organizationId)) === '1';
  } catch (e) {
    return false;
  }
}

/**
 * Shown at the top of the calendar when the organization's Google Calendar authorisation has
 * expired (google_calendar_status === 'token_expired'). Admins get a one-click reconnect that
 * runs the same OAuth popup as the settings card; others are told to ask an admin.
 * Dismissal lasts for the browser session only: the sync is silently dead until reconnected.
 */
function GoogleCalendarAuthBanner({ t }) {
  const navigate = useNavigate();
  const { currentOrganization, patchCurrentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;
  const isAdmin = can(currentOrganization, 'settings.integrations');
  const expired =
    !!currentOrganization?.google_calendar_beta_enabled &&
    currentOrganization?.google_calendar_status === 'token_expired';

  const [dismissed, setDismissed] = React.useState(() => readDismissed(organizationId));
  React.useEffect(() => {
    setDismissed(readDismissed(organizationId));
  }, [organizationId]);

  const onConnected = React.useCallback(() => {
    patchCurrentOrganization({ google_calendar_status: 'connected', google_calendar_enabled: true });
  }, [patchCurrentOrganization]);

  const { connect, connecting, error } = useGoogleCalendarConnect({ organizationId, t, onConnected });

  if (!expired || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(dismissKey(organizationId), '1');
    } catch (e) {
      // Session storage unavailable: the banner simply comes back on the next page load.
    }
  };

  return (
    <Alert
      severity="warning"
      data-testid="google-calendar-auth-banner"
      onClose={handleDismiss}
      sx={{ mx: { xs: 1, md: 2 }, mb: 0.5, flexShrink: 0, alignItems: 'flex-start' }}
    >
      <AlertTitle sx={{ fontWeight: 600 }}>{t('googleCalendar.authExpiredTitle')}</AlertTitle>
      <Typography variant="body2" sx={{ mb: isAdmin ? 1 : 0 }}>
        {isAdmin ? t('googleCalendar.authExpiredBody') : t('googleCalendar.authExpiredBodyNonAdmin')}
      </Typography>
      {isAdmin && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            size="small"
            color="warning"
            data-testid="google-calendar-reconnect-button"
            startIcon={connecting ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
            onClick={connect}
            disabled={connecting}
          >
            {connecting ? t('googleCalendar.connecting') : t('googleCalendar.reconnect')}
          </Button>
          <Button size="small" color="inherit" onClick={() => navigate('/settings?tab=integrations')}>
            {t('googleCalendar.openSettings')}
          </Button>
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }} data-testid="google-calendar-banner-error">
          {error}
        </Typography>
      )}
    </Alert>
  );
}

export default withTranslation()(GoogleCalendarAuthBanner);
